/**
 * Product photographs.
 *
 * Types, limits and key derivation. No bytes and no image library: this
 * package stays dependency-free so that the browser can import it for the same
 * validation the server applies (which is how the catalogue already checks a
 * barcode's check digit twice with one implementation).
 *
 * The bytes live in object storage — ADR-0015.
 */
/**
 * What is stored, per image.
 *
 * The uploaded original is not kept. A 12-megapixel photograph from a phone is
 * four megabytes nobody will ever display, and Master Specification section 27
 * targets machines with 4 GB of RAM. 1600 px re-derives anything a catalogue
 * screen or a printed label needs.
 */
export const RENDITIONS = {
    thumb: { longestEdge: 320, quality: 74 },
    full: { longestEdge: 1600, quality: 82 },
};
export const RENDITION_NAMES = Object.keys(RENDITIONS);
export const isRendition = (value) => typeof value === 'string' && Object.hasOwn(RENDITIONS, value);
/** Everything is stored as WebP: one decoder path, and the smallest bytes. */
export const IMAGE_CONTENT_TYPE = 'image/webp';
/**
 * What may be uploaded.
 *
 * Checked by decoding, never by trusting the declared `Content-Type` — a file
 * named `photo.jpg` that is not an image must not reach the store. This list
 * is what the file input advertises and what the error message names; the
 * decoder is what enforces it.
 */
export const ACCEPTED_UPLOAD_FORMATS = [
    'jpeg',
    'png',
    'webp',
    'avif',
    'gif',
    'tiff',
];
export const isAcceptedUploadFormat = (value) => value !== undefined &&
    ACCEPTED_UPLOAD_FORMATS.includes(value);
/** For the browser's file picker. HEIC is absent because the decoder cannot read it. */
export const ACCEPTED_UPLOAD_MIME_TYPES = 'image/jpeg,image/png,image/webp,image/avif,image/gif,image/tiff';
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/**
 * Twelve photographs per product.
 *
 * A number rather than no limit, because the gallery is loaded whole and an
 * unbounded one is a page that takes a minute to open on the hardware this
 * targets. Twelve is generous for a catalogue and small enough to render at
 * once.
 */
export const MAX_IMAGES_PER_PRODUCT = 12;
/**
 * A guard against decompression bombs.
 *
 * A 60.000 × 60.000 PNG can be a few kilobytes on disk and 14 GB decoded, so
 * the byte limit alone does not protect the process.
 */
export const MAX_SOURCE_PIXELS = 8000 * 8000;
export const MAX_ALT_TEXT_LENGTH = 200;
/**
 * Where an image's renditions live.
 *
 * The tenant leads, so a key from one tenant cannot address another tenant's
 * object even if a row somehow named the wrong prefix — a misrouted read is a
 * miss rather than someone else's photograph.
 *
 * The last segment is a random token rather than the row id, for two reasons.
 * It is known before the row exists, so the bytes and the reference are written
 * in one direction with no placeholder in between; and an object key is not
 * derivable from an id that appears in URLs, which matters on the day signed
 * URLs or a public bucket enter the picture.
 */
export const storagePrefixFor = (tenantId, productId, storageToken) => `tenants/${tenantId}/catalog/products/${productId}/images/${storageToken}`;
export const renditionKey = (storagePrefix, rendition) => `${storagePrefix}/${rendition}.webp`;
export const allRenditionKeys = (storagePrefix) => RENDITION_NAMES.map((rendition) => renditionKey(storagePrefix, rendition));
/**
 * The aspect ratio a gallery reserves before the bytes arrive.
 *
 * Section 27 forbids layout that jumps; knowing width and height without
 * fetching the image is what avoids it.
 */
export const aspectRatio = (image) => image.width / image.height;
//# sourceMappingURL=images.js.map